import { useState, useEffect, useCallback } from 'react'
import { useWalletStore } from '@/store'
import { authService } from '@/services/auth'
import { userProfileService, type UserProfile } from '@/services/firebase'

interface UseAuthReturn {
  // State
  isAuthenticated: boolean
  isAuthenticating: boolean
  userProfile: UserProfile | null
  error: string | null

  // Actions
  authenticate: () => Promise<boolean>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

/**
 * Hook for managing wallet-based authentication
 */
export function useAuth(): UseAuthReturn {
  const { connected, publicKey, isAuthenticated } = useWalletStore()

  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Load user profile when authenticated
  useEffect(() => {
    if (isAuthenticated && publicKey) {
      loadUserProfile()
    } else {
      setUserProfile(null)
    }
  }, [isAuthenticated, publicKey])

  // NOTE: Auto-authenticate removed - user must explicitly click to sign
  // This avoids confusion between wallet connect and message signing steps

  const loadUserProfile = async () => {
    if (!publicKey) return

    try {
      const profile = await userProfileService.getOrCreateProfile(publicKey)
      setUserProfile(profile)
    } catch (err) {
      console.error('Failed to load user profile:', err)
    }
  }

  const authenticate = useCallback(async (): Promise<boolean> => {
    if (!connected || !publicKey) {
      setError('Wallet not connected')
      return false
    }

    if (isAuthenticated) {
      return true
    }

    setIsAuthenticating(true)
    setError(null)

    try {
      const result = await authService.authenticate()

      if (result.success) {
        // Load user profile after successful auth
        await loadUserProfile()
        return true
      }

      return false
    } catch (err: any) {
      console.error('Authentication failed:', err)
      setError(err.message || 'Authentication failed')
      return false
    } finally {
      setIsAuthenticating(false)
    }
  }, [connected, publicKey, isAuthenticated])

  const signOut = useCallback(async () => {
    try {
      await authService.signOut()
      setUserProfile(null)
      setError(null)
    } catch (err: any) {
      console.error('Sign out failed:', err)
      setError(err.message || 'Sign out failed')
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (isAuthenticated && publicKey) {
      await loadUserProfile()
    }
  }, [isAuthenticated, publicKey])

  return {
    isAuthenticated,
    isAuthenticating,
    userProfile,
    error,
    authenticate,
    signOut,
    refreshProfile,
  }
}
